import { useState, useRef } from "react";
import classNames from "classnames";
import dropdownStyles from "../edit-task-modal.module.css";
import styles from "./task-tag-editor.module.css";
import { AddTagIcon, EditTagsIcon, TagVisibleIcon, TagHiddenIcon } from "../edit-task-modal-icons";
import { TaskColorPicker } from "./task-color-picker";
import { createTag } from "shared/datamodel/metadata";
import { dbKey } from "shared/util/utils";
import { TagType, Tag } from "shared/datamodel/schemas/metadata";
import { ITaskController } from "../../../controller";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  FloatingArrow,
  useHover,
  useInteractions,
  arrow,
} from "@floating-ui/react";
import CloseIcon from "frontend/ui-components/design-system/icons/close-icon";
import { Tooltip } from "frontend/ui-components/design-system/tooltip/tooltip";
import { useTextTruncation } from "frontend/hooks/use-text-truncation";
import { onlyWhitespace } from "frontend/canvas-designer-new/text-element";

export function TaskTagEditor({
  controller,
  tagEntries,
  onTagChanged,
}: {
  controller: ITaskController;
  tagEntries: [string, Tag][];
  onTagChanged?: () => void;
}) {
  const [isEditingTags, setIsEditingTags] = useState(false);

  return (
    <div className={classNames(styles.tagEditor, dropdownStyles.dropdownPopup)}>
      <TagCreator controller={controller} />
      {isEditingTags ? (
        <TagEditor allTags={tagEntries} controller={controller} />
      ) : (
        <TagSelector allTags={tagEntries} controller={controller} onTagChanged={onTagChanged} />
      )}
      <button className={styles.editTagsButton} onClick={() => setIsEditingTags((previous) => !previous)}>
        {!isEditingTags && <EditTagsIcon />}
        <span>{isEditingTags ? "Close" : "Edit Tags"}</span>
      </button>
    </div>
  );
}

function TagCreator({ controller }: { controller: ITaskController }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#1e1e1e");
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: colorPickerOpen,
    onOpenChange: setColorPickerOpen,
    placement: "left",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const handleAddTag = () => {
    if (!onlyWhitespace(name) && controller.context.reflect) {
      const tagData = createTag(name.trim(), color);
      const tagId = dbKey(tagData.id, TagType);

      controller.context.reflect.mutate.putMetadata({
        id: tagId,
        value: tagData.value,
      });

      setName("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAddTag();
    }
  };

  return (
    <>
      <div className={styles.tagCreatorContainer} ref={refs.setReference}>
        <div className={styles.tagCreatorInputRow}>
          <button
            className={styles.tagColorButton}
            style={{ backgroundColor: color }}
            type="button"
            {...getReferenceProps()}
          />
          <input
            className={styles.tagCreatorInput}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a tag"
          />
          {name.trim() && <AddTagButton onClick={handleAddTag} />}
        </div>
        {colorPickerOpen && (
          <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
            <TaskColorPicker
              onChange={(newColor) => {
                setColor(newColor);
                setColorPickerOpen(false);
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}

function TagSelector({
  allTags,
  controller,
  onTagChanged,
}: {
  allTags: [string, Tag][];
  controller: ITaskController;
  onTagChanged?: () => void;
}) {
  const sortedTags = [...allTags].sort((a, b) => {
    const aKey = a[1].order ?? a[1].lastModifiedTimestamp ?? 0;
    const bKey = b[1].order ?? b[1].lastModifiedTimestamp ?? 0;
    return aKey - bKey;
  });

  if (sortedTags.length === 0) {
    return null;
  }

  return (
    <div className={styles.tagsList}>
      {sortedTags.map(([tagId, tag]) => (
        <TagSelectorItem key={tagId} tagId={tagId} tag={tag} controller={controller} onTagChanged={onTagChanged} />
      ))}
    </div>
  );
}

function TagSelectorItem({
  tagId,
  tag,
  controller,
  onTagChanged,
}: {
  tagId: string;
  tag: Tag;
  controller: ITaskController;
  onTagChanged?: () => void;
}) {
  const nameRef = useRef<HTMLSpanElement>(null);
  const isTextTruncated = useTextTruncation(nameRef, [tag.name]);

  const isSelected = controller.isTagSelected(tagId);

  return (
    <Tooltip text={tag.name} disabled={!isTextTruncated} offset={8} maxWidth={250}>
      <div
        className={styles.tagItem}
        style={{
          color: isSelected ? "white" : "#1e1e1e",
          backgroundColor: isSelected ? tag.color : "transparent",
          borderColor: isSelected ? undefined : tag.color,
        }}
        onClick={() => {
          controller.toggleTag(tagId);
          onTagChanged?.();
        }}
      >
        <span ref={nameRef} className={styles.tagName}>
          {tag.name}
        </span>
        {isSelected ? <TagVisibleIcon /> : <TagHiddenIcon />}
      </div>
    </Tooltip>
  );
}

function AddTagButton({ onClick }: { onClick: () => void }) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    placement: "top",
    middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { open: 100, close: 100 },
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

  return (
    <>
      <button
        ref={refs.setReference}
        className={styles.addTagButton}
        type="button"
        onClick={onClick}
        title="Add tag"
        {...getReferenceProps()}
      >
        <AddTagIcon />
      </button>
      {isTooltipOpen && (
        <div ref={refs.setFloating} style={floatingStyles} className={styles.addTagTooltip} {...getFloatingProps()}>
          Add Tag
          <FloatingArrow ref={arrowRef} context={context} tipRadius={2} width={10} height={5} />
        </div>
      )}
    </>
  );
}

function TagEditor({ allTags, controller }: { allTags: [string, Tag][]; controller: ITaskController }) {
  const sortedTags = [...allTags].sort((a, b) => {
    const aKey = a[1].order ?? a[1].lastModifiedTimestamp ?? 0;
    const bKey = b[1].order ?? b[1].lastModifiedTimestamp ?? 0;
    return aKey - bKey;
  });

  const handleDeleteTag = (tagId: string) => {
    if (controller.context.reflect) {
      const fullTagId = dbKey(tagId, TagType);
      controller.context.reflect.mutate.deleteMetadata({ id: fullTagId });
    }
  };

  const handleUpdateTag = (tagId: string, updatedTag: Tag) => {
    if (controller.context.reflect) {
      const fullTagId = dbKey(tagId, TagType);
      controller.context.reflect.mutate.putMetadata({
        id: fullTagId,
        value: updatedTag,
      });
    }
  };

  const handleColorChange = (tagId: string, newColor: string) => {
    if (controller.context.reflect) {
      const tag = allTags.find(([id]) => id === tagId)?.[1];
      if (tag) {
        const updatedTag = { ...tag, color: newColor };
        handleUpdateTag(tagId, updatedTag);
      }
    }
  };

  const handleNameChange = (tagId: string, newName: string) => {
    if (controller.context.reflect) {
      const tag = allTags.find(([id]) => id === tagId)?.[1];
      if (tag) {
        const updatedTag = { ...tag, name: newName };
        handleUpdateTag(tagId, updatedTag);
      }
    }
  };

  return (
    <div className={styles.tagEditorList}>
      {sortedTags.map(([tagId, tag]) => (
        <TagEditRow
          key={tagId}
          tag={tag}
          onDelete={() => handleDeleteTag(tagId)}
          onColorChange={(newColor) => handleColorChange(tagId, newColor)}
          onNameChange={(newName) => handleNameChange(tagId, newName)}
        />
      ))}
    </div>
  );
}

function TagEditRow({
  tag,
  onDelete,
  onColorChange,
  onNameChange,
}: {
  tag: Tag;
  onDelete: () => void;
  onColorChange: (color: string) => void;
  onNameChange: (name: string) => void;
}) {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(tag.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);
  const isTextTruncated = useTextTruncation(nameRef, [tag.name, isEditingName]);

  const {
    refs: colorPickerRefs,
    floatingStyles,
    context,
  } = useFloating({
    open: colorPickerOpen,
    onOpenChange: setColorPickerOpen,
    placement: "left",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const handleStartEditing = () => {
    setIsEditingName(true);
    setEditedName(tag.name);
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const handleFinishEditing = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== tag.name) {
      onNameChange(trimmedName);
    } else {
      setEditedName(tag.name);
    }
    setIsEditingName(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleFinishEditing();
    } else if (e.key === "Escape") {
      setEditedName(tag.name);
      setIsEditingName(false);
    }
  };

  return (
    <>
      <div className={styles.tagEditRow}>
        <div className={styles.tagEditRowContent}>
          <button
            className={styles.tagColorButton}
            style={{ backgroundColor: tag.color }}
            type="button"
            ref={colorPickerRefs.setReference}
            {...getReferenceProps()}
          />

          {isEditingName ? (
            <input
              ref={inputRef}
              className={styles.tagEditNameInput}
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleFinishEditing}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <Tooltip text={tag.name} disabled={!isTextTruncated} offset={14} maxWidth={250}>
              <div className={styles.tagEditName} onClick={handleStartEditing}>
                <span ref={nameRef}>{tag.name}</span>
              </div>
            </Tooltip>
          )}
        </div>

        <button className={styles.tagDeleteButton} onClick={onDelete} title="Delete tag" type="button">
          <CloseIcon width={8} height={8} strokeWidth={1.2} />
        </button>

        {colorPickerOpen && (
          <div ref={colorPickerRefs.setFloating} style={floatingStyles} {...getFloatingProps()}>
            <TaskColorPicker
              onChange={(newColor) => {
                onColorChange(newColor);
                setColorPickerOpen(false);
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}
